// Security utilities for form protection

export interface SecurityConfig {
  maxSubmitAttempts: number;
  submitCooldown: number;
  minTimeOnPage: number;
  minInteractions: number;
  rateLimitWindow: number;
}

export const defaultSecurityConfig: SecurityConfig = {
  maxSubmitAttempts: 5,
  submitCooldown: 60000, // 1 minute
  minTimeOnPage: 3000, // 3 seconds
  minInteractions: 2,
  rateLimitWindow: 30000 // 30 seconds
};

// Detect suspicious patterns in form data
export const detectSuspiciousContent = (formData: Record<string, any>): boolean => {
  const suspiciousPatterns = [
    // Common spam patterns
    /<script/gi, // Script tags
    /<iframe/gi, // Iframe tags
    /javascript:/gi, // JavaScript protocols
    /onclick/gi, // Event handlers
    /viagra|casino|lottery|winner|prize|money|free|click here/gi, // Spam keywords
    /\b[A-Z]{15,}\b/g, // Long uppercase strings
    /(.)\1{15,}/g, // Repeated characters (15+ times)
  ];

  const textFields = ['fullName', 'email', 'message', 'theme'];
  
  for (const field of textFields) {
    const value = formData[field];
    if (typeof value === 'string') {
      for (const pattern of suspiciousPatterns) {
        if (pattern.test(value)) {
          console.warn(`Suspicious content detected in ${field}:`, value);
          return true;
        }
      }
    }
  }

  return false;
};

// Validate email format more strictly
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const disposableEmailDomains = [
    '10minutemail.com',
    'tempmail.org',
    'guerrillamail.com',
    'mailinator.com',
    'throwaway.email'
  ];

  if (!emailRegex.test(email)) {
    return false;
  }

  const domain = email.split('@')[1]?.toLowerCase();
  if (disposableEmailDomains.includes(domain)) {
    console.warn('Disposable email detected:', email);
    return false;
  }

  return true;
};

// Check if form submission timing seems human-like
export const validateSubmissionTiming = (
  timeOnPage: number,
  interactions: number,
  config: SecurityConfig
): { isValid: boolean; reason?: string } => {
  if (timeOnPage < config.minTimeOnPage) {
    return {
      isValid: false,
      reason: `Submission too fast. Minimum time required: ${config.minTimeOnPage / 1000}s`
    };
  }

  if (interactions < config.minInteractions) {
    return {
      isValid: false,
      reason: `Insufficient interactions. Minimum required: ${config.minInteractions}`
    };
  }

  // Check if submission is too fast (less than 5 seconds)
  if (timeOnPage < 5000) {
    return {
      isValid: false,
      reason: 'Submission timing appears automated'
    };
  }

  return { isValid: true };
};

// Generate a simple client-side challenge
export const generateChallenge = (): { challenge: string; answer: string } => {
  const operations = ['+', '-', '*'];
  const operation = operations[Math.floor(Math.random() * operations.length)];
  
  let num1: number, num2: number, answer: number;
  
  switch (operation) {
    case '+':
      num1 = Math.floor(Math.random() * 50) + 1;
      num2 = Math.floor(Math.random() * 50) + 1;
      answer = num1 + num2;
      break;
    case '-':
      num1 = Math.floor(Math.random() * 50) + 25;
      num2 = Math.floor(Math.random() * 25) + 1;
      answer = num1 - num2;
      break;
    case '*':
      num1 = Math.floor(Math.random() * 10) + 1;
      num2 = Math.floor(Math.random() * 10) + 1;
      answer = num1 * num2;
      break;
    default:
      num1 = 2;
      num2 = 2;
      answer = 4;
  }

  return {
    challenge: `${num1} ${operation} ${num2} = ?`,
    answer: answer.toString()
  };
};

// Store and retrieve rate limiting data
export const getRateLimitData = (key: string) => {
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

export const setRateLimitData = (key: string, data: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // Silently fail if localStorage is not available
  }
};

export const clearRateLimitData = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Silently fail if localStorage is not available
  }
};
